import { assignSafe, SDuration } from '@summit/core';
import { DurationFormatter, SummitFormattedText } from '@summit/models';
import { Duration } from 'dayjs/plugin/duration';
import { EldHoursMethod, EldHoursMethodMapping } from './eld-hours-method.enum';

export class ReconcileParameters {
  areDriversEnteringLogs: boolean = null;
  applyRoutesFromSchedule: boolean = null;
  autoMoveToDay: boolean = null;
  //ELD Logs
  eldHoursMethod: EldHoursMethod;
  eldDeduction: Duration;
  enforcedDotBreakRequirement: boolean = null;
  dotBreakIncludePartialTime: boolean = null;
  minDetentionTime: Duration = null;
  minOnDutyBeforeDetention: Duration = null;

  constructor(jsonData?: Partial<ReconcileParameters>) {
    if (jsonData) {
      assignSafe(this, jsonData);
      if (jsonData.eldDeduction) this.eldDeduction = SDuration.new(jsonData.eldDeduction);
      if (jsonData.minDetentionTime) this.minDetentionTime = SDuration.new(jsonData.minDetentionTime);
      if (jsonData.minOnDutyBeforeDetention)
        this.minOnDutyBeforeDetention = SDuration.new(jsonData.minOnDutyBeforeDetention);
    }
    this.applyRoutesFromSchedule ??= null;
    this.autoMoveToDay ??= null;
    this.eldHoursMethod ??= null;
    //console.log(`Reconcile Parameters : ${jsonData?.eldHoursMethod} => ${this.eldHoursMethod}`);
  }

  setCorporateDefaults() {
    this.areDriversEnteringLogs ??= true;
    this.applyRoutesFromSchedule ??= false;
    this.autoMoveToDay ??= true;
    this.eldHoursMethod ??= EldHoursMethod.HosTypeBased;
    this.enforcedDotBreakRequirement ??= true;
    this.dotBreakIncludePartialTime ??= false;
  }

  isEquivalent(other: ReconcileParameters): boolean {
    if (!other) return false;
    return (
      this.areDriversEnteringLogs === other.areDriversEnteringLogs &&
      this.applyRoutesFromSchedule === other.applyRoutesFromSchedule &&
      this.autoMoveToDay === other.autoMoveToDay &&
      this.eldHoursMethod === other?.eldHoursMethod &&
      this.eldDeduction?.asMilliseconds() === other.eldDeduction?.asMilliseconds() &&
      this.enforcedDotBreakRequirement === other.enforcedDotBreakRequirement &&
      this.dotBreakIncludePartialTime === other.dotBreakIncludePartialTime &&
      this.minDetentionTime?.asMilliseconds() === other.minDetentionTime?.asMilliseconds() &&
      this.minOnDutyBeforeDetention?.asMilliseconds() === other.minOnDutyBeforeDetention?.asMilliseconds()
    );
  }

  toFormattedText(corporate: boolean): SummitFormattedText {
    const response = new SummitFormattedText();

    if (this.areDriversEnteringLogs) response.AddKeyValues('Drivers Entering Logs', 'Yes');
    else if (this.areDriversEnteringLogs === false) response.AddKeyValues('Drivers Entering Logs', 'No');
    else response.AddKeyValues('Drivers Entering Logs', '[Corp Default]');

    if (this.applyRoutesFromSchedule) response.AddKeyValues('Routes from Schedule', 'Yes');
    else if (this.applyRoutesFromSchedule === false) response.AddKeyValues('Routes from Schedule', 'No');
    else response.AddKeyValues('Routes from Schedule', '[Corp Default]');

    if (this.autoMoveToDay) response.AddKeyValues('Auto Move to Correct Day', 'Yes');
    else if (this.autoMoveToDay === false) response.AddKeyValues('Auto Move to Correct Day', 'No');
    else response.AddKeyValues('Auto Move to Correct Day', '[Corp Default]');

    if (this.eldHoursMethod || this.eldHoursMethod === 0)
      response.AddKeyValues('ELD Logic', EldHoursMethodMapping[this.eldHoursMethod]);
    else response.AddKeyValues('ELD Logic', '[Corp Default]');

    if (this.eldDeduction) response.AddKeyValues('ELD Deduction', DurationFormatter(this.eldDeduction));
    else response.AddKeyValues('ELD Deduction', '[Corp Default]');

    if (this.enforcedDotBreakRequirement) response.AddKeyValues('Enforced DOT Break', 'Yes');
    else if (this.enforcedDotBreakRequirement === false) response.AddKeyValues('Enforced DOT Break', 'No');
    else response.AddKeyValues('Enforced DOT Break', '[Corp Default]');

    if (this.enforcedDotBreakRequirement !== false) {
      if (this.dotBreakIncludePartialTime) response.AddKeyValues('Include Partial Time', 'Yes');
      else if (this.dotBreakIncludePartialTime === false) response.AddKeyValues('Include Partial Time', 'No');
      else response.AddKeyValues('Include Partial Time', '[Corp Default]');
    }

    if (SDuration.isDuration(this.minDetentionTime))
      response.AddKeyValues('Min Detention Time', DurationFormatter(this.minDetentionTime));
    else if (!corporate) response.AddKeyValues('Min Detention Time', '[Corp Default]');
    if (SDuration.isDuration(this.minOnDutyBeforeDetention))
      response.AddKeyValues('Min On-Duty Before Detention', DurationFormatter(this.minOnDutyBeforeDetention));
    else if (!corporate) response.AddKeyValues('Min On-Duty Before Detention', '[Corp Default]');
    return response;
  }
}
